import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Skeleton,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClusterData } from 'store';
import { setActiveTab, setMatchColorsToGraph } from 'store/slices/segmenterSlice';
import Chart from "../Chart/Chart";
import SegmenterChat from "../SegmenterChat/SegmenterChat";
import classes from "./Segmenter.module.scss";
import Typography from '@mui/material/Typography';
import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import DownloadIcon from "@mui/icons-material/Download";
import { exportToCSV } from '../../utils/exportToCSV';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import VirtualTable from "../SimpleTable/VirtualTable";
import SimpleTable from "components/SimpleTable/SimpleTable";
import { Tabs, Tab, Box, IconButton, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { v4 as uuidv4 } from 'uuid';
import { ClientColors } from "utils/app_colors";
import { useClient } from "utils/client_info";
import { jsonToCsvString } from "../../assets/CSVHelperFunctions";

export const removeKeysFromObjects = (arr, keysToRemove) => {
  return (arr || []).map((obj) => {
    const newObj = {};
    for (const key in obj) {
      if (!keysToRemove.includes(key)) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  });
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1, height: '100%' }}>
          <Typography sx={{ height: '100%' }}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function TabContentComponent({ dataObject }) {
  return (
    <Box sx={{ p: 1 }}>
      {dataObject}
    </Box>
  );
}


const Segmenter = () => {
  const { clientName } = useClient();
  const [expanded, setExpanded] = useState([]);
  const dispatch = useDispatch();
  const {
    clusterData,
    clusterOutputString,
    numericData,
    rawData,
    columnData,
    aggregatedCombinedData,
    loading,
    categoricalData,
    activeTab,
    matchColorsToGraph,
  } = useSelector((state) => state.segmenter);
  const initialChartIds = [uuidv4()];

  const handleChange = (event, newValue) => {
    dispatch(setActiveTab(newValue));
  }

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const fetchData = useCallback(async () => {
    try {
      await Promise.all([dispatch(fetchClusterData())]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!clusterData) {
      fetchData();
    }
  }, [dispatch, fetchData, clusterData]);

  const handleDownload = () => {
    const csvString = jsonToCsvString(clusterData);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Segment_breakdown.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => {
      if (isExpanded) {
        return [...prev, panel];
      } else {
        return prev.filter((p) => p !== panel);
      }
    });
  };

  const getColorPalette = (index, source) => {
    const colorsLength = 10;
    const color = ClientColors[clientName][source][index];
    index = (index + 1) % colorsLength;
    return color;
  };

  const handleSwitchChange = (event) => {
    dispatch(setMatchColorsToGraph(event.target.checked));
  };

  const accordions =
    loading || !clusterData
      ? Array.from({ length: 3 }).map((_, index) => (
        <Accordion key={index}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Skeleton variant="text" width="80%" height={30} />
          </AccordionSummary>
          <AccordionDetails>
            <Skeleton variant="rectangular" width="100%" height={60} />
          </AccordionDetails>
        </Accordion>
      ))
      : Object.keys(clusterData).map((clusterKey, index) => {
        const cluster = clusterData[clusterKey];
        const panelKey = `panel${index}`;
        return (
          <Accordion
            key={clusterKey}
            expanded={expanded.includes(panelKey)}
            onChange={handleAccordionChange(panelKey)}
            sx={
              matchColorsToGraph
                ? {
                  border: `1px solid ${getColorPalette(index, 'stroke')}`,
                  background: getColorPalette(index, 'fill'),
                }
                : undefined
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {cluster.title}
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <p>{cluster.description}</p>
              <ul className={classes.keyPoints}>
                {cluster.key_points.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>
            </AccordionDetails>
          </Accordion>
        );
      });

  const [chatExpanded, setChatExpanded] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const updatedRawData = removeKeysFromObjects(rawData, ["CDP_ID"]);
  const updatedAggregatedCombinedData = removeKeysFromObjects(
    aggregatedCombinedData,
    ["CDP_ID", "Cluster_Population"]
  );

  const [charts, setCharts] = useState(initialChartIds.map((id) => ({ chartId: id })));

  const handleAddChart = () => {
    const nextId = uuidv4();
    setCharts((prev) => [...prev, { chartId: nextId }]);
  };

  const handleCloseChart = (e, index) => {
    e.stopPropagation();
    setCharts((prev) => {
      const next = prev.slice(0, index).concat(prev.slice(index + 1));
      return next;
    });
  };

  const getWidthClass = (idx) => {
    const index = idx - 1;
    const total = charts?.length - 1 ?? 0;
    if (total === 1) return 'cardFull';
    if (total % 2 === 1 && index === total - 1) return 'cardFull';
    return 'cardHalf';
  };

  return (
    <>
      <Box component="section" className={charts.length > 1 ? classes.audienceContainerExpanded : classes.audienceContainer} >
        <Box component="section" className={classes.audienceSubContainer}>
          <Box component="section" className={chatExpanded ? classes.chatGridExpanded : classes.chatGrid}>
            <div className={classes.headline}>
              <h3 className={classes.title}>Audience Assistant</h3>
              {!loading && (
                <button
                  className={classes.chatExpanderButton}
                  onClick={() => setChatExpanded(!chatExpanded)}
                >
                  <span>{chatExpanded ? <WestIcon /> : <EastIcon />}</span>
                </button>
              )}
            </div>
            {loading ? (
              <>
                <Skeleton variant="text" width="100%" height={30} />
                <Skeleton variant="text" width="100%" height={30} />
                <Skeleton variant="text" width="100%" height={30} />
              </>
            ) : (
              <SegmenterChat clusterOutputString={clusterOutputString} />
            )}
          </Box>
          <Box component="section" className={chatExpanded ? classes.graphGridHidden : classes.graphGrid}>
            <div className={classes.tables} style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Static Tabs Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 8, background: '#fff', flexShrink: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                  style={{ flex: 1 }}
                >
                  <Tab label="Segment Breakdown" />
                  <Tab label="Customer Personas" />
                  <Tab label="Customer 360" />
                </Tabs>
                {activeTab === 0 && (
                  <button
                    style={{ background: 'none', border: 'none', padding: 0, marginLeft: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', height: 36 }}
                    onClick={() => { handleDownload(); showSnackbar("Download successful! You have downloaded the Segment Data."); }}
                    title="Download as CSV"
                  >
                    <DownloadIcon fontSize="medium" />
                  </button>
                )}
                {activeTab === 1 && (
                  <button
                    style={{ background: 'none', border: 'none', padding: 0, marginLeft: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', height: 36 }}
                    onClick={() => { exportToCSV(updatedAggregatedCombinedData, "customer_personas.csv", "Cluster"); showSnackbar("Download successful! You have downloaded the Customer Personas data as CSV."); }}
                    title="Download Customer Personas as CSV"
                  >
                    <DownloadIcon fontSize="medium" />
                  </button>
                )}
                {activeTab === 2 && (
                  <button
                    style={{ background: 'none', border: 'none', padding: 0, marginLeft: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', height: 36 }}
                    onClick={() => { exportToCSV(updatedRawData, "customer_360.csv", "Cluster"); showSnackbar("Download successful! You have downloaded all the records of the Customer 360 data."); }}
                    title="Download as CSV"
                  >
                    <DownloadIcon fontSize="medium" />
                  </button>
                )}
              </div>
              {/* Scrollable Tab Content */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <TabPanel value={activeTab} index={0}>
                  {loading ? (
                    <Skeleton />
                  ) : (
                    <TabContentComponent dataObject={accordions} />
                  )}
                </TabPanel>
                <TabPanel value={activeTab} index={1} className={classes.tabPanel}>
                  {loading ? (
                    <Skeleton />
                  ) : (
                    <SimpleTable
                      data={updatedAggregatedCombinedData}
                      // title="Customer Personas"
                      firstColumn="Cluster"
                    />
                  )}
                </TabPanel>
                <TabPanel value={activeTab} index={2} className={classes.tabPanel}>
                  {loading ? (
                    <Skeleton />
                  ) : (
                    <VirtualTable
                      data={updatedRawData}
                      // title="Customer 360"
                      firstColumn="Cluster"
                    />
                  )}
                </TabPanel>
              </div>
            </div>
            <Box component="section" className={classes.graphs} >
              <Box
                component={"section"}
                className={classes.tabLabel}
              >
                <h3 className={classes.title}>Interactive Plotting</h3>

                <div>

                  Match colors <Checkbox
                    checked={matchColorsToGraph} onChange={handleSwitchChange} color={"primary"}
                  />
                  <IconButton
                    aria-label="add chart"
                    onClick={handleAddChart}
                    disabled={loading}
                    className={classes.addChartIcon}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </Box>
              <Box className={classes.tabChart}>
                {loading || !columnData ? (
                  <Skeleton variant="text" width="100%" height={30} />
                ) : (
                  <Chart
                    chartId={charts[0]?.chartId}
                    numericData={numericData}
                    categoricalData={categoricalData}
                    columnData={columnData}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box component="section" className={classes.cardsContainer}>
          {columnData && charts.map((chart, idx) => {
            return idx !== 0 && (
              <div key={chart.chartId} className={classes[getWidthClass(idx)]}>
                <Box component="section" className={classes.card}>
                  <div
                    className={classes.cardNav}
                  >
                    <Typography className={classes.cardHeader} variant="button">Interactive Plotting</Typography>
                    {charts.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={`close chart ${idx}`}
                        onClick={(e) => handleCloseChart(e, idx)}
                        onMouseDown={(e) => e.stopPropagation()}
                        className={classes.closeChartButton}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                  <Chart
                    key={`${chart.chartId}`}
                    chartId={chart.chartId}
                    numericData={numericData}
                    categoricalData={categoricalData}
                    columnData={columnData}
                    isCardHalf={getWidthClass(idx) === 'cardHalf'}
                  />
                </Box>
              </div>
            )
          })}
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default Segmenter;